import {ReportDefinitionV1} from '../contracts/report-definition.types';
import {ReportTaxonomy} from '../contracts/report-taxonomy.types';
import {orderedDataColumnIds} from '../ReportTable/useReportTableColumns';
import {labelForField} from '../ReportTable/fieldLabels';
import {groupingCellPaths, GroupingToken, isProducibleGroupingToken} from '../ReportTable/useClientGrouping';
import {grainRootOf} from './definitionFields';
import {englishLabel, FormatLabel, PROJECT_STATUS_EM_DASH, projectStatusCandidates} from '../projectStatusLabels';
import {projectStageCandidates} from '../projectStageLabels';
import {hoursScript, lookupScript, projectIdScript, unitColumnScript, unitVaryingScript} from './exportCellScripts';

export type ExportFormat = 'DATE' | 'FLOAT' | 'HOURS' | 'MINUTES' | 'INTEGER' | 'STRING' | 'TIMESTAMP';
export type ExportProcessor = 'AS_IS' | 'CONCATENATION' | 'LIST' | 'MULTIPLYING' | 'SCRIPT' | 'CUSTOM_FIELD';

export interface ExportSubProcessor {
	format: ExportFormat;
	fieldNames: string[];
	delimiter: string;
}

/** `LIST` reads `arrayPath` + `subProcessor`; `SCRIPT` reads `script` (see `exportCellScripts`). */
export interface ExportProcessorData {
	arrayPath?: string;
	subProcessor?: ExportSubProcessor;
	script?: string;
}

export interface DataProcessorInput {
	fieldNames: string[];
	label: string;
	format: ExportFormat;
	processor: ExportProcessor;
	processorData?: ExportProcessorData;
}

/** One entry of the export's `orderBy`. Carries its own direction: the backend applies each entry
 * independently, so a report sorted descending on screen exports descending rather than being
 * silently flipped to ascending. An absent value sorts last in either direction. */
export interface ExportSort {
	field: string;
	direction: 'ASC' | 'DESC';
}

export interface ReportExportInputs {
	labels: string[];
	dataProcessorInputs: DataProcessorInput[];
	orderBy: ExportSort[];
}

/** A flat CSV row cannot express parent/child nesting, so hierarchy travels as two always-present
 * columns: the task's own id and its parent's id. The backend emits rows depth-first, so a
 * consumer can reconstruct the tree from these two columns alone. */
const TASK_ID_PATH = 'task.companyTaskId';
const PARENT_TASK_ID_PATH = 'task.parent.companyTaskId';
const ID_COLUMN_PATHS = [TASK_ID_PATH, PARENT_TASK_ID_PATH];

const ASSIGNEES_ARRAY_PATH = 'task.assignees';
const ASSIGNEES_PATH_PREFIX = `${ASSIGNEES_ARRAY_PATH}.`;
const ASSIGNEE_FIRST_NAME_PATH = `${ASSIGNEES_ARRAY_PATH}.firstName`;
const ASSIGNEE_LAST_NAME_PATH = `${ASSIGNEES_ARRAY_PATH}.lastName`;
/** The row model only ever emits ONE cell for assignees — `task.assignees.firstName` (a `persons`
 * cell listing every assignee's name; see `reportRowMapper.flattenToPathKeys`) — even though the
 * field tree can offer both `firstName` and `lastName` as separately selectable leaf paths. So
 * whichever assignee leaf path(s) end up selected, the export must still collapse to exactly this
 * one representative column, matching what the table itself actually renders. */
const ASSIGNEE_DISPLAY_PATH = ASSIGNEE_FIRST_NAME_PATH;

/** The project-status grain's two compound sub-entities. Both arrive as an ARRAY of leaf-lists
 * (`labels: [[{id},{name},{color}], ...]`), which is exactly the shape `AS_IS` cannot read: its
 * `DataNodeList.getByPath` descent is gated on `DataNode.hasChildren()`, true only for a
 * `DataNodeList` and not for a `List` of them. The path then terminates on the compound itself and
 * the cell receives the Java record's `toString`. `LIST` + `arrayPath` is the shape that reads
 * them, the same one the assignee column above uses. */
const LABELS_ARRAY_PATH = 'projectStatus.labels';

/** The task grain's own labels column. It arrives in the identical array-of-leaf-lists shape as the
 * project-status labels above, but is selected as the `.name` leaf path (`task.labels.name`) rather
 * than the bare array path, because that is how the Columns dropdown offers it. Routed through the
 * same LIST processor for the same reason: `AS_IS` cannot descend into the array and would resolve
 * `name` on the task itself, putting the task's own name in the Label column instead of its labels. */
const TASK_LABELS_ARRAY_PATH = 'task.labels';
const TASK_LABEL_NAME_PATH = `${TASK_LABELS_ARRAY_PATH}.name`;

const OWNER_ARRAY_PATH = 'projectStatus.owner';
const OWNER_FIRST_NAME_PATH = `${OWNER_ARRAY_PATH}.firstName`;
const OWNER_LAST_NAME_PATH = `${OWNER_ARRAY_PATH}.lastName`;
/** The catalog's key for the owner column. It names a `name` leaf the entity does not have - the
 * person leaves are firstName/lastName - so it is a display key, not a wire path. */
const OWNER_DISPLAY_PATH = 'projectStatus.owner.name';

/** The display-key suffix report-service expands on `/data/fetch` only. The export fetches the
 * literal path, which names no leaf, so a column sent under it would be empty on every row. */
const DISPLAY_KEY_SUFFIX = '.info';

/** Durations the catalogs declare, named outright so an export raised before the catalog resolves
 * still writes hours rather than minutes. The catalog, when present, is what decides. */
const FALLBACK_HOURS_PATHS = new Set([
	'task.reported.time',
	'projectStatus.hours.total',
	'projectStatus.hours.billable',
	'projectStatus.hours.nonBillable',
	'projectStatus.retainer.target',
	'projectStatus.retainer.consumed',
	'projectStatus.retainer.remaining',
]);
const FALLBACK_UNIT_VARYING_PATHS = new Set(['task.work.estimate', 'task.work.remaining', 'task.derived.variance']);

const ESTIMATION_UNIT_PATH = 'task.project.estimationUnit';
const RAG_PATH = 'projectStatus.rag';
const STAGE_PATH = 'projectStatus.stage';

const PROJECT_ID_LABEL = 'Project ID';
const UNIT_COLUMN_LABEL = 'Estimate unit';
const HOURS_SUFFIX = ' (h)';

/** Labels for synthetic columns this module always adds that have no entry in
 * `DEFAULT_FIELD_LABELS` (which only covers real, user-selectable field paths). Task ID itself
 * already has a `DEFAULT_FIELD_LABELS` entry ('Task ID'), so only the parent id needs one here. */
const SYNTHETIC_COLUMN_LABELS: Record<string, string> = {
	[PARENT_TASK_ID_PATH]: 'Parent task ID',
};

/** Resolves a column's display label the way the table does, plus this module's own synthetic
 * columns, so the file's headings read exactly like the ones on screen. */
function labelFor(path: string, taxonomy: ReportTaxonomy | null | undefined): string {
	return SYNTHETIC_COLUMN_LABELS[path] ?? labelForField(path, taxonomy);
}

/** Prepends Task ID / Parent task ID as the first two columns, de-duplicated against whatever the
 * user already selected (dropping any later occurrence rather than adding a second one).
 *
 * Only for the task grain. The two columns exist so a flat file can carry parent/child nesting, and
 * a grain with no hierarchy has none to carry: adding them to a project-status export produces two
 * leading columns headed Task ID that are empty on every row. */
function withIdColumnsPrepended(paths: string[], root: string): string[] {
	if (root !== 'task') {
		return paths;
	}
	const rest = paths.filter(path => !ID_COLUMN_PATHS.includes(path));
	return [...ID_COLUMN_PATHS, ...rest];
}

/** Collapses every `task.assignees.*` path in `paths` down to the single canonical
 * `ASSIGNEE_DISPLAY_PATH`, at the position of its first occurrence — so selecting both
 * `task.assignees.firstName` and `task.assignees.lastName` as columns (the field tree allows both
 * as separate leaves) still produces exactly ONE assignee column, matching the standard
 * task-report CSV export and the table's own single assignee cell. */
function collapseAssigneeColumns(paths: string[]): string[] {
	let sawAssigneeColumn = false;
	const result: string[] = [];
	for (const path of paths) {
		if (path.startsWith(ASSIGNEES_PATH_PREFIX)) {
			if (!sawAssigneeColumn) {
				result.push(ASSIGNEE_DISPLAY_PATH);
				sawAssigneeColumn = true;
			}
			continue;
		}
		result.push(path);
	}
	return result;
}

/** Appends any grouping cell path not already present as a column. A flat CSV cannot nest, so
 * grouping is client-side only; the server can still sort by a grouping path, but only if that
 * path is actually a column in the export, hence the append. */
function withGroupingColumnsAppended(paths: string[], groupingPaths: string[]): string[] {
	const seen = new Set(paths);
	const result = [...paths];
	for (const path of groupingPaths) {
		if (!seen.has(path)) {
			seen.add(path);
			result.push(path);
		}
	}
	return result;
}

/** Builds the one assignee `dataProcessorInputs` entry, mirroring `TaskReportCsvUtil.getCsvLabels`'s
 * assignee column exactly: `LIST` over `task.assignees`, concatenating each assignee's `firstName`
 * + `lastName` with a space. Fixed shape regardless of which assignee leaf path(s) the definition
 * actually selected — `collapseAssigneeColumns` guarantees at most one assignee column reaches
 * here. */
function assigneeDataProcessor(label: string): DataProcessorInput {
	return {
		fieldNames: [ASSIGNEE_FIRST_NAME_PATH, ASSIGNEE_LAST_NAME_PATH],
		label,
		format: 'STRING',
		processor: 'LIST',
		processorData: {
			arrayPath: ASSIGNEES_ARRAY_PATH,
			subProcessor: {
				format: 'STRING',
				fieldNames: ['firstName', 'lastName'],
				delimiter: ' ',
			},
		},
	};
}

/** Builds a `LIST` `dataProcessorInputs` entry for a compound labels array, joining each item's
 * `name` with no delimiter (multiple labels concatenate directly, matching the standard task-report
 * CSV export). Shared by the project-status and task grains' labels columns, which differ only in
 * `arrayPath`. */
function labelsListProcessor(arrayPath: string, label: string): DataProcessorInput {
	return {
		fieldNames: [`${arrayPath}.name`],
		label,
		format: 'STRING',
		processor: 'LIST',
		processorData: {
			arrayPath,
			subProcessor: {format: 'STRING', fieldNames: ['name'], delimiter: ''},
		},
	};
}

/** The owner column: `LIST` over `projectStatus.owner`, joining first and last name. */
function ownerDataProcessor(label: string): DataProcessorInput {
	return {
		fieldNames: [OWNER_FIRST_NAME_PATH, OWNER_LAST_NAME_PATH],
		label,
		format: 'STRING',
		processor: 'LIST',
		processorData: {
			arrayPath: OWNER_ARRAY_PATH,
			subProcessor: {format: 'STRING', fieldNames: ['firstName', 'lastName'], delimiter: ' '},
		},
	};
}

function asIs(path: string, label: string): DataProcessorInput {
	return {fieldNames: [path], label, format: 'STRING', processor: 'AS_IS'};
}

function scriptColumn(fieldNames: string[], label: string, script: string): DataProcessorInput {
	return {fieldNames, label, format: 'STRING', processor: 'SCRIPT', processorData: {script}};
}

function tableOf(candidates: {id: string; label: string}[]): Record<string, string> {
	return Object.fromEntries(candidates.map(candidate => [candidate.id, candidate.label]));
}

/** The RAG table, with the statusless state written as an empty cell. The table's em dash is a
 * display device for "we do not know"; in a file it is a non-ASCII character that Excel opens as
 * mojibake (the CSV carries no BOM), and a blank already says the same thing a null RAG does. */
function ragTable(formatLabel: FormatLabel): Record<string, string> {
	return tableOf(
		projectStatusCandidates(formatLabel).map(candidate =>
			candidate.label === PROJECT_STATUS_EM_DASH ? {...candidate, label: ''} : candidate
		)
	);
}

type DurationKind = 'hours' | 'unitVaries' | null;

/** Whether a column is a duration, and which kind. `dataType: 'duration'` leaves are minutes on the
 * wire; a `unitVaries` number leaf is minutes on an HOURS project and points on a POINTS one. */
function durationKindOf(path: string, taxonomy: ReportTaxonomy | null | undefined): DurationKind {
	const declared = taxonomy?.fields.find(field => field.key === path);
	if (declared) {
		if (declared.unitVaries === true && declared.dataType === 'number') return 'unitVaries';
		if (declared.dataType === 'duration') return 'hours';
		return null;
	}
	if (FALLBACK_UNIT_VARYING_PATHS.has(path)) return 'unitVaries';
	if (FALLBACK_HOURS_PATHS.has(path)) return 'hours';
	return null;
}

/** The path the server sorts on for a column. A project or role display key names no leaf on the
 * export fetch, so sorting or grouping by one would read nothing on every row and leave the file in
 * fetch order; both sort by the entity's name instead, as the Project / Role column reads. */
function orderPathFor(path: string): string {
	if (path.endsWith(DISPLAY_KEY_SUFFIX)) {
		const owner = path.slice(0, -DISPLAY_KEY_SUFFIX.length);
		if (owner.endsWith('.project') || owner.endsWith('.role')) {
			return `${owner}.name`;
		}
	}
	return path;
}

interface ColumnContext {
	taxonomy: ReportTaxonomy | null | undefined;
	formatLabel: FormatLabel;
	/** Flips once the unit column has been emitted, so it appears once per file. */
	unitColumnEmitted: boolean;
}

/**
 * The export column(s) for one selected path, written the way the table shows the value.
 *
 * - The assignee, labels and owner columns get their `LIST` shapes.
 * - A project display key becomes two columns, Project ID (the pill's id) and Project (the name);
 *   a role display key becomes the role name. Sent as-is, a display key names no leaf.
 * - RAG and Stage are relabelled with the same vocabulary the table uses, in the active locale.
 * - A duration is written in hours with `(h)` on its header. A unit-varying one is hours or points
 *   per row, and the first of them brings an Estimate unit column saying which.
 * - Everything else, budget type included, is passed through as-is.
 */
function columnsFor(path: string, label: string, context: ColumnContext): DataProcessorInput[] {
	if (path === ASSIGNEE_DISPLAY_PATH) return [assigneeDataProcessor(label)];
	if (path === LABELS_ARRAY_PATH) return [labelsListProcessor(LABELS_ARRAY_PATH, label)];
	if (path === TASK_LABEL_NAME_PATH) return [labelsListProcessor(TASK_LABELS_ARRAY_PATH, label)];
	if (path === OWNER_DISPLAY_PATH) return [ownerDataProcessor(label)];

	if (path.endsWith(DISPLAY_KEY_SUFFIX)) {
		const owner = path.slice(0, -DISPLAY_KEY_SUFFIX.length);
		if (owner.endsWith('.project')) {
			const custom = `${owner}.customProjectId`;
			const company = `${owner}.companyProjectId`;
			return [
				scriptColumn([custom, company], PROJECT_ID_LABEL, projectIdScript(custom, company)),
				asIs(`${owner}.name`, label),
			];
		}
		if (owner.endsWith('.role')) {
			return [asIs(`${owner}.name`, label)];
		}
	}

	if (path === RAG_PATH) {
		return [scriptColumn([path], label, lookupScript(path, ragTable(context.formatLabel)))];
	}
	if (path === STAGE_PATH) {
		return [scriptColumn([path], label, lookupScript(path, tableOf(projectStageCandidates(context.formatLabel))))];
	}

	const kind = durationKindOf(path, context.taxonomy);
	if (kind === 'hours') {
		return [scriptColumn([path], `${label}${HOURS_SUFFIX}`, hoursScript(path))];
	}
	if (kind === 'unitVaries') {
		const column = scriptColumn([path, ESTIMATION_UNIT_PATH], label, unitVaryingScript(path, ESTIMATION_UNIT_PATH));
		if (context.unitColumnEmitted) return [column];
		context.unitColumnEmitted = true;
		return [column, scriptColumn([ESTIMATION_UNIT_PATH], UNIT_COLUMN_LABEL, unitColumnScript(ESTIMATION_UNIT_PATH))];
	}

	return [asIs(path, label)];
}

/**
 * Turns an AI-report-builder report definition (plus the active client-side grouping) into the
 * `{labels, dataProcessorInputs, orderBy}` shape report-service's ad-hoc export takes. Pure: no
 * network, no React — the caller supplies the taxonomy already resolved, if it wants the
 * taxonomy-label fallback.
 *
 * Column set/order: Task ID and Parent task ID always come first (a flat CSV cannot nest, so
 * these two columns are how a consumer reconstructs hierarchy), followed by the user's selected
 * columns (`definition.layout` order, falling back to `definition.fields` order), followed by any
 * active grouping's cell path that is not already one of those columns (the server sorts by a
 * grouping path, so it must actually be present in the export or the sort has nothing to read).
 *
 * `hiddenColumnPaths` are the columns the user has switched off in ColumnManager, which are dropped
 * from the export so the file carries the columns the table on screen is showing. Passed in rather
 * than read off the definition because hiding is live table state: `layout` records order and width
 * only, and a column absent from `layout` may equally be one the agent never laid out, which must
 * still export. The two id columns are structural (a flat CSV reconstructs nesting from them) and are
 * never dropped, and a grouping column is still appended even when hidden — a CSV sorted by a group
 * key that does not carry the key is not readable.
 *
 * Hiding is applied before the assignee leaves collapse, so the single assignee column exports when
 * ANY assignee leaf is still visible. That follows the table: it renders one assignee cell for
 * whichever leaves are selected, so hiding one of two leaves does not remove the cell either.
 *
 * Each column is written the way the table shows it - see `columnsFor`. So one selected path can
 * become more than one column (Project ID + Project, Estimate + Estimate unit), and `labels` is read
 * off the built columns rather than off the paths. `formatLabel` translates the RAG and Stage words;
 * without one they are English.
 *
 * `filter` and `hierarchical` are NOT built here — those are the data source's job.
 */
export function buildReportExportInputs(
	definition: ReportDefinitionV1,
	grouping: GroupingToken[],
	taxonomy?: ReportTaxonomy | null,
	hiddenColumnPaths?: readonly string[],
	formatLabel: FormatLabel = englishLabel
): ReportExportInputs {
	const root = grainRootOf(definition);
	const cellPaths = groupingCellPaths(root, taxonomy);
	// Only the levels the table actually applies. A token the grain does not publish resolves to no
	// cell path, and one whose field the catalog does not publish is dropped from the table with a
	// notice; sorting the file by either, or appending its column, would contradict the screen.
	const groupingPaths = grouping
		.filter(token => isProducibleGroupingToken(token, cellPaths, taxonomy))
		.map(token => cellPaths[token]);

	const hidden = new Set(hiddenColumnPaths ?? []);
	const visiblePaths = orderedDataColumnIds(definition).filter(path => !hidden.has(path));
	const selectedPaths = collapseAssigneeColumns(withIdColumnsPrepended(visiblePaths, root));
	const columnPaths = withGroupingColumnsAppended(selectedPaths, groupingPaths);

	const context: ColumnContext = {taxonomy, formatLabel, unitColumnEmitted: false};
	const dataProcessorInputs = columnPaths.flatMap(path => columnsFor(path, labelFor(path, taxonomy), context));
	const labels = dataProcessorInputs.map(input => input.label);

	// Grouping is client-side and always ascending; the definition's own sort carries the direction the
	// user chose. Grouping comes first so grouped rows arrive contiguous and the sort orders within them.
	const orderBy: ExportSort[] = groupingPaths.map(field => ({field: orderPathFor(field), direction: 'ASC'}));
	if (definition.sort?.field) {
		orderBy.push({
			field: orderPathFor(definition.sort.field),
			direction: definition.sort.direction === 'DESC' ? 'DESC' : 'ASC',
		});
	}

	return {labels, dataProcessorInputs, orderBy};
}

/**
 * The filename a CSV export downloads as.
 *
 * `Util.exportToCSV` assigns what it is given straight to an anchor's `download` attribute and
 * appends nothing, so the bare report name downloaded a file with no extension - which no
 * spreadsheet opens on a double-click, and which macOS shows as a document of unknown type. A name
 * that already ends in `.csv` is left alone rather than becoming `report.csv.csv`.
 */
export function csvFileName(reportName: string): string {
	const trimmed = reportName.trim();
	const base = trimmed === '' ? 'report' : trimmed;
	return base.toLowerCase().endsWith('.csv') ? base : `${base}.csv`;
}
